<template>
	<div>
		<SongsSwitcher />
		<RootSide />
		<ExternalSearchInput v-if="externalSearchOpened" ref="externalSearchInput" />
		<ExternalSearchSide v-if="externalSearchOpened" @scroll="handleExternalSearchSideScroll" />
		<SongsSide v-if="!externalSearchOpened" />
		<MainBar />
		<Detail v-if="currentSong" />
		<Intro v-if="!currentSong" />

		<SearchLocalModal />
		<LoginRegistrationModal />
		<LoginRegistrationForceModal />
		<CreatePlaylistModal />
		<UserSettingsModal v-if="currentUser" />
		<SongPlaylistsModal />
		<UpdateModal />
		<UpdateIosModal />
		<UpdateAndroidModal />
		<DeleteSongModal />
		<EditSongModal v-if="currentSong" />
		<SettingsMenu />
		<PlaylistsSettingsModal />
		<MembershipModal />
		<SessionSettingsModal />
		<LookSettingsModal />
		<MobileLandingModal />
		<NeedMembershipModal />
		<LanguageSettingsModal />
		<ControlSettingsModal />
		<ExportModal />
	</div>
</template>

<script>
	import { mapState } from "vuex";
	import MainBar from "@/components/mainbar/MainBar";
	import RootSide from "@/components/root-side/RootSide"
	import SongsSide from "@/components/songs-side/SongsSide"
	import SongsSwitcher from "@/components/songs-switcher/SongsSwitcher";
	import ExternalSearchSide from "@/components/external-search-side/ExternalSearchSide";
	import ExternalSearchInput from "@/components/external-search-side/ExternalSearchInput";
	import Detail from "@/components/detail/Detail";
	import Intro from "@/components/intro/Intro";
	import SearchLocalModal from "@/components/search-local/SearchLocalModal";
	import LoginRegistrationModal from "@/components/auth/LoginRegistrationModal";
	import CreatePlaylistModal from "@/components/create-playlist/CreatePlaylistModal";
	import UserSettingsModal from "@/components/settings/UserSettingsModal";
	import SettingsMenu from "@/components/settings/SettingsMenu";
	import UpdateModal from "@/components/update-modal/UpdateModal";
	import DeleteSongModal from "@/components/delete-song-modal/DeleteSongModal";
	import EditSongModal from "@/components/edit-song-modal/EditSongModal";
	import SongPlaylistsModal from "@/components/song-playlists/SongPlaylistsModal"
	import PlaylistsSettingsModal from "@/components/settings/PlaylistsSettingsModal"
	import config from "@/config/config";
	import MembershipModal from "@/components/settings/MembershipModal";
	import SessionSettingsModal from "@/components/settings/SessionSettingsModal";
	import { SessionService } from "@/services/SessionService";
	import LookSettingsModal from "../components/settings/LookSettingsModal";
	import MobileLandingModal from "../components/mobile-landing/MobileLandingModal";
	import UpdateIosModal from "../components/update-modal/UpdateIosModal";
	import { App } from "@capacitor/app";
	import UpdateAndroidModal from "../components/update-modal/UpdateAndroidModal";
	import NeedMembershipModal from "../components/need-membership-modal/NeedMembershipModal";
	import Vue from "vue";
	import LoginRegistrationForceModal from "../components/auth/LoginRegistrationForceModal";
	import LanguageSettingsModal from "../components/settings/LanguageSettingsModal";
	import ExportModal from "../components/settings/ExportModal";
	import {ExternalSearchService} from "@/services/ExternalSearchService";
	import ControlSettingsModal from "@/components/settings/ControlSettingsModal";

	export default {
		components: {
			ControlSettingsModal,
			ExportModal,
			LanguageSettingsModal,
			LoginRegistrationForceModal,
			NeedMembershipModal,
			UpdateAndroidModal,
			UpdateIosModal,
			MobileLandingModal,
			LookSettingsModal,
			MembershipModal,
			MainBar,
			RootSide,
			SongsSide,
			SongsSwitcher,
			SearchLocalModal,
			ExternalSearchSide,
			ExternalSearchInput,
			UserSettingsModal,
			LoginRegistrationModal,
			CreatePlaylistModal,
			Intro,
			UpdateModal,
			Detail,
			SongPlaylistsModal,
			DeleteSongModal,
			EditSongModal,
			SettingsMenu,
			PlaylistsSettingsModal,
			SessionSettingsModal
		},
		computed: {
			...mapState([ "externalSongs", "externalSearchOpened", "currentSong", "currentUser" ])
		},
		async beforeRouteEnter(to, from, next) {
			if (from.name !== null) return next();
			const songId = to.params.song;
			if (songId) {
				const song = await ExternalSearchService.getSong(songId)
				song.transpose = 0
				if (song) return next(vm => vm.$store.commit("setCurrentSong", song));
				else return next();
			}

			next();
		},
		async beforeRouteUpdate(to, from, next) {
			const songId = to.params.song;
			if (songId in this.$store.state.songs) {
				this.$store.commit("setCurrentSong", this.$store.state.songs[songId]);
				if (this.$store.state.session !== null) {
					SessionService.setSong(this.$store.state.currentSong);
				}
			} else {
				const externalSong = this.externalSongs.find(s => songId === s.id)
				if (externalSong) this.$store.commit("setCurrentSong", externalSong);
			}
			next();
		},
		methods: {
			checkUpdate() {
				if (navigator.userAgent.toLowerCase().indexOf(' electron/') > -1) return;

				if (this.$store.state.device && this.$store.state.networkOnline) {
					if (this.$store.state.variables.webVersion && this.$store.state.device.platform === "web") {
						if (parseInt(this.$store.state.variables.webVersion) > config.webVersion) {
							this.$modal.show("update");
						}
					}

					if (this.$store.state.variables.iosVersion && this.$store.state.device.platform === "ios") {
						if (parseInt(this.$store.state.variables.iosVersion) > config.iosVersion) {
							this.$modal.show("update-ios");
						}
					}

					if (this.$store.state.variables.androidVersion && this.$store.state.device.platform === "android") {
						if (parseInt(this.$store.state.variables.androidVersion) > config.androidVersion) {
							this.$modal.show("update-android");
						}
					}
				}
			},
			handleExternalSearchSideScroll() {
				if (this.$refs.externalSearchInput) {
					this.$refs.externalSearchInput.handleScroll()
				}
			}
		},
		created() {
			App.addListener("appStateChange", (state) => {
				if (!state.isActive) return;
				this.checkUpdate();
			});

			document.addEventListener("CheckUpdate", () => {
				this.checkUpdate();
			});

			document.addEventListener("LoggedIn", () => {
				Vue.nextTick(() => {
					if (this.currentUser === null || this.currentUser.isAnonymous) {
						if (this.$store.state.device.platform === "web" || this.$store.state.device.platform === "desktop-win" || this.$store.state.device.platform === "desktop-mac") {
							if ("noreg" in this.$route.query) return;
							this.$modal.show("login-registration-force", { need: true });
						}
					}
				});
			});
		},
		mounted() {
		}
	}
</script>

<style lang="scss">
</style>
